import {
  FilledLinkToWebField,
  LinkField,
  LinkType,
  PrismicDocument,
} from "@prismicio/client";
import { useSinglePrismicDocument } from "@prismicio/react";
import { useMemo } from "react";

type LinksKeys =
  | "cgu"
  | "cgv"
  | "politique_de_confidentialite"
  | "gestion_des_cookies"
  | "mentions_legales";

type PrismicLinks = Record<LinksKeys, LinkField>;

type LinkItem = {
  readonly value: string;
  readonly label: string;
};

type Links = Partial<Record<LinksKeys, LinkItem>>;

const LABELS: Record<LinksKeys, string> = {
  cgu: "Conditions générales d'utilisation",
  cgv: "Conditions générales de vente",
  politique_de_confidentialite: "Politique de confidentialité",
  gestion_des_cookies: "Gestion des cookies",
  mentions_legales: "Mentions légales",
} as const;

/**
 * Hook pour récupérer les liens depuis Prismic CMS
 * @returns Un objet contenant les liens avec leurs labels associés
 */
export const useLinks = (): Links => {
  const [document] =
    useSinglePrismicDocument<PrismicDocument<PrismicLinks>>("links");

  return useMemo(() => {
    const links = document?.data;

    if (!links) {
      return {};
    }

    const entries = Object.entries(links) as Array<[LinksKeys, LinkField]>;

    return entries.reduce<Links>((acc, [key, link]) => {
      // Ne garde que les liens web valides
      if (link?.link_type === LinkType.Web) {
        acc[key] = {
          value: (link as FilledLinkToWebField).url,
          label: LABELS[key],
        };
      }
      return acc;
    }, {});
  }, [document?.data]);
};
